import React from 'react'

import {
  AlignItems,
  Body2,
  Body3,
  BorderStyle,
  Box,
  ButtonColorVariant,
  Color,
  Display,
  FlexDirection,
  Heading3,
  Heading5,
  Icon,
  IconVariant,
  JustifyContent,
  Modal,
  ModalClose,
  ModalFooter,
  ModalHeader,
  RadioGroup,
  Space,
  useModal,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'

import type { BoxSizeOption } from '@library/api-customer-engagement-experience'

import { FormattedText } from '../helpers/FormattedText'
import { ModalConfirmButton } from './ConfirmButtons'
import { useStagedSelection } from './useStagedSelection'

import onePersonIcon from '../assets/box-size/1-person.svg'
import twoPersonIcon from '../assets/box-size/2-person.svg'
import threePersonIcon from '../assets/box-size/3-person.svg'
import fourPersonIcon from '../assets/box-size/4-person.svg'
import fivePersonIcon from '../assets/box-size/5-person.svg'

export const BOX_SIZE_MODAL_NAME = 'RESUBSCRIBE-BOX-SIZE-MODAL'

// Keeps the radio dot at the top of the label, so it doesn't drift to the middle when a row
// expands.
const StyledRadioContainer = withCitrusTheme(Box, () => ({
  '& label': {
    alignItems: 'flex-start',
  },
}))

const NoticeBox = withCitrusTheme(Box, () => ({
  borderRadius: '8px',
}))

// Icons for each box size, keyed by portion count.
const PORTION_ICONS: Record<number, string> = {
  1: onePersonIcon,
  2: twoPersonIcon,
  3: threePersonIcon,
  4: fourPersonIcon,
  5: fivePersonIcon,
}

type BoxSizeModalProps = {
  options: BoxSizeOption[]
  selectedBoxSize: { numPortions: number }
  title: string
  suitableForTitle: string
  saveLabel: string
  onSelect: (boxSize: { numPortions: number }) => void
}

export const BoxSizeModal = ({
  options,
  selectedBoxSize,
  title,
  suitableForTitle,
  saveLabel,
  onSelect,
}: BoxSizeModalProps) => {
  const { closeCurrentModal } = useModal()
  const [stagedNumPortions, setStagedNumPortions] = useStagedSelection(
    BOX_SIZE_MODAL_NAME,
    selectedBoxSize.numPortions,
  )

  const canSave = stagedNumPortions !== selectedBoxSize.numPortions

  const getRadioLabel = (option: BoxSizeOption) => {
    const icon = PORTION_ICONS[option.numPortions]
    const isStaged = option.numPortions === stagedNumPortions

    return (
      <Box display={Display.Flex} flexDirection={FlexDirection.Column} flexGrow={1} width="100%">
        <Box
          display={Display.Flex}
          alignItems={AlignItems.Center}
          justifyContent={JustifyContent.SpaceBetween}
          paddingH={3}
          paddingRight={0}
          width="100%"
        >
          <Heading5>{option.title}</Heading5>
          {icon && <img src={icon} alt="" width={44} height={26} />}
        </Box>
        {isStaged && (
          <Box paddingH={3}>
            <Space size={1} direction="vertical" />
            <Body2 color={Color.ColdGrey_800}>
              <FormattedText text={option.recipesLabel} />
            </Body2>
            <Space size={1} direction="vertical" />
            <Body2 color={Color.ColdGrey_800}>{suitableForTitle}</Body2>
            <Space size={1} direction="vertical" />
            {option.suitableFor.map((label) => (
              <Box key={label} display={Display.Flex} alignItems={AlignItems.Center}>
                <Icon name="tick" variant={IconVariant.Confirmation} size={4} />
                <Space direction="horizontal" size={2} />
                <Body2 color={Color.ColdGrey_800}>{label}</Body2>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    )
  }

  const onSave = () => {
    onSelect({ numPortions: stagedNumPortions })
    closeCurrentModal()
  }

  return (
    <Modal name={BOX_SIZE_MODAL_NAME} onRequestClose={closeCurrentModal}>
      <ModalHeader>
        <Heading3>{title}</Heading3>
        <ModalClose onClose={closeCurrentModal} />
      </ModalHeader>
      <Box paddingH={[4, 6]} paddingV={[4, 6]} paddingBottom={[0, 0]}>
        <StyledRadioContainer>
          <RadioGroup
            outline
            value={stagedNumPortions}
            onChange={(event) => setStagedNumPortions(Number(event.target.value))}
            options={options.map((option) => ({
              name: 'box-size',
              value: option.numPortions,
              label: () => getRadioLabel(option),
            }))}
          />
        </StyledRadioContainer>
        {(() => {
          const stagedOption = options.find((opt) => opt.numPortions === stagedNumPortions)
          return (
            <>
              {stagedOption?.notice && (
                <>
                  <Space size={3} direction="vertical" />
                  <NoticeBox
                    display={Display.Flex}
                    bg={Color.Informative_50}
                    paddingH={4}
                    paddingV={4}
                    alignItems={AlignItems.FlexStart}
                    borderColor={Color.Informative_50}
                    borderWidth={0.5}
                    borderStyle={BorderStyle.Solid}
                    borderRadius={1.5}
                    gap={2}
                  >
                    <Icon name="info" variant={IconVariant.Informative} />
                    <Body3 color={Color.ColdGrey_800}>
                      <FormattedText text={stagedOption.notice} />
                    </Body3>
                  </NoticeBox>
                </>
              )}
            </>
          )
        })()}
      </Box>
      <ModalFooter>
        <ModalConfirmButton
          colorVariant={ButtonColorVariant.Primary}
          width="100%"
          onClick={onSave}
          disabled={!canSave}
        >
          {saveLabel}
        </ModalConfirmButton>
      </ModalFooter>
    </Modal>
  )
}
